import type { useTranslation } from "@/hooks/useTranslation";
import type { StepCounts } from "@/lib/chat-thinking-trace";

const MS_IN_S = 1_000;

/**
 * Opening and folding slide over 200 ms instead of snapping, the panel's own
 * height measured by Radix. Off for readers who asked for less motion.
 */
export const COLLAPSE_ANIMATION_CLASS =
  "overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up motion-reduce:animate-none";

type Translate = ReturnType<typeof useTranslation>["t"];

/** A counter as the card shows it, with the key it keeps while it climbs. */
export type CounterSegment = { readonly key: string; readonly text: string };

/** The time a row shows: what it ran, and since when it runs, if it does. */
export type RowTime = {
  readonly finishedMs: number;
  /** When the pass running now started, on this browser's clock. */
  readonly runningSince?: number;
};

/** A finished duration in the whole seconds the card shows. */
export function shownSeconds(ms: number): number {
  return Math.round(ms / MS_IN_S);
}

/** The counters of a step, in the reader's language. */
export function countSegments(
  counts: StepCounts,
  t: Translate,
  isTraceRunning: boolean
): CounterSegment[] {
  // While the reader waits, the pages found are the number that climbs; once
  // the trace settled, the pages handed over are the ones they can open.
  const isFoundCounted = isTraceRunning && counts.foundCount > 0;
  const segments: (CounterSegment | false)[] = [
    counts.queryCount > 0 && {
      key: "queries",
      text: t("chat.message.research.searchCount", { count: counts.queryCount }),
    },
    counts.companyCount > 0 && {
      key: "companies",
      text: t(
        isTraceRunning
          ? "chat.message.research.companyFoundCount"
          : "chat.message.research.companyCount",
        { count: counts.companyCount }
      ),
    },
    counts.pageCount > 0 && {
      key: "pages",
      text: t("chat.message.research.pageCount", { count: counts.pageCount }),
    },
    isFoundCounted
      ? {
          key: "sources",
          text: t("chat.message.research.urlCount", { count: counts.foundCount }),
        }
      : counts.sourceCount > 0 && {
          key: "sources",
          text: t("chat.message.research.sourceCount", {
            count: counts.sourceCount,
          }),
        },
  ];
  return segments.filter((segment): segment is CounterSegment => Boolean(segment));
}

